import useDownloadCSV from "hooks/useDownloadCSV";
import useFilterHook from "hooks/useFilter";
import usePagination from "hooks/usePagination";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { AREA_COLUMNS_DATA, AREA_FILTER_DEFINITIONS } from "./config";
import { downloadAreaCSV, fetchArea } from "services/area";
import { DEFAULT_TOTAL_RECORDS, MODALS, STALE_TIME } from "utils/constants";
import FilterArea from "./FilterArea";
import AddArea from "./AddArea";
import Delete from "./DeleteArea";
import { FormButton } from "components/Common/Buttons";
import Modal from "components/Common/Modal";
import Table from "components/Common/Table/index";
import Loader from "components/Common/loader";
import {
  fetchCommissionReportLedger,
  fetchCommissionReportLedgerDashboard,
} from "services/commissionLedger";

const CommissionReportLedger = () => {
  const [isAddAreaOpen, setIsAddAreaOpen] = useState(false);
  const [rowDataForEdit, setRowDataForEdit] = useState(null);
  const [isDelete, setIsDelete] = useState(-1);
  const [isSuperFranchise] = useState(() =>
    Number(localStorage.getItem("is_super_franchise"))
  );
  const [isFranchiseEmployee] = useState(() =>
    Number(localStorage.getItem("is_franchise_employee"))
  );
  const [isSubFranchise] = useState(() =>
    Number(localStorage.getItem("sub_franchise"))
  );

  const {
    currentPage,
    itemPerPage,
    disablePrev,
    disableNext,
    disableFirst,
    disableLast,
    totalPages,
    updateCurrentPage,
    updateTotalRecords,
  } = usePagination();

  const DownloadButton = useDownloadCSV(downloadAreaCSV, "area.csv");

  const { filters, updateFilter, updateMultipleFilters } = useFilterHook(
    AREA_FILTER_DEFINITIONS,
    updateCurrentPage
  );

  const filtersRef = useRef(filters);

  const editArea = useCallback((row) => {
    setRowDataForEdit(row);
    setIsAddAreaOpen(true);
  }, []);

  const deleteRow = useCallback((row) => {
    setIsDelete(row.area_id);
  }, []);

  console.log("Filters in main page:", filters);

  const {
    data: areaData,
    isLoading,
    refetch: refetchAreas,
  } = useQuery(
    ["fetch-commission-rledger", currentPage, filters.month, filters.year],
    () => fetchCommissionReportLedger(currentPage, filters),
    {
      enabled: !!(filters.month && filters.year),
      keepPreviousData: false,
      staleTime: STALE_TIME,
      onSuccess: (res) => {
        updateTotalRecords(res.data?.additional_data?.total_count);
      },
      select: (res) => {
        const response = res?.data?.data;
        if (response && response.length) {
          res.data.data = response.map((x, index) => ({
            ...x,
            _index: (currentPage - 1) * DEFAULT_TOTAL_RECORDS + index + 1,
          }));
        }
        return res;
      },
      onError: (res) => {
        console.error("Error fetching areas:", res);
      },
    }
  );

  const {
    data: cardsData,
    isLoadingCard,
    refetch: refetchCards,
  } = useQuery(
    ["fetch-commission-report-ledger", currentPage],
    () => fetchCommissionReportLedgerDashboard(currentPage, filters),
    {
      keepPreviousData: false,
      staleTime: STALE_TIME,
      onSuccess: (res) => {
        updateTotalRecords(res.data?.additional_data?.total_count);
      },
      select: (res) => {
        const response = res?.data?.data;
        if (response && response.length) {
          res.data.data = response.map((x, index) => ({
            ...x,
            _index: (currentPage - 1) * DEFAULT_TOTAL_RECORDS + index + 1,
          }));
        }
        return res;
      },
      onError: (res) => {
        console.error("Error fetching areas:", res);
      },
    }
  );

  const onClose = useCallback(
    (refetch = false) => {
      if (refetch) refetchAreas();
      setIsAddAreaOpen(false);
      setRowDataForEdit(null);
    },
    [refetchAreas]
  );

  const onCloseDelete = useCallback(
    (refetch = false) => {
      if (refetch) refetchAreas();
      setIsDelete(-1);
    },
    [refetchAreas]
  );

  const columns = useMemo(() => {
    return AREA_COLUMNS_DATA({
      deleteRow,
      editArea,
      filters,
      isSuperFranchise,
      isFranchiseEmployee,
      isSubFranchise,
    });
  }, [
    deleteRow,
    editArea,
    isSuperFranchise,
    isFranchiseEmployee,
    isSubFranchise,
    filters.is_active,
  ]);

  useEffect(() => {
    if (filtersRef.current !== filters) {
      refetchAreas();
      filtersRef.current = filters;
    }
  }, [filters, refetchAreas]);

  const loadNextPage = useCallback(() => {
    updateCurrentPage(currentPage + 1);
  }, [currentPage, updateCurrentPage]);

  const loadPreviousPage = useCallback(() => {
    updateCurrentPage(currentPage - 1);
  }, [currentPage, updateCurrentPage]);

  const loadFirstPage = useCallback(() => {
    if (currentPage !== 1) {
      updateCurrentPage(1);
    }
  }, [currentPage, updateCurrentPage]);

  const loadLastPage = useCallback(() => {
    if (currentPage !== totalPages) {
      updateCurrentPage(totalPages);
    }
  }, [currentPage, totalPages, updateCurrentPage]);

  if (isLoading) {
    return <Loader isLoading={isLoading} />;
  }

  const transformDataForTable = (data) => {
    return Object.entries(data).map(([date, commission], index) => ({
      _index: index + 1,
      id: index + 1,
      date: date,
      commission: commission,
    }));
  };

  const tableData = transformDataForTable(areaData?.data || {});

  return (
    <div className="container-fluid mb-3">
      <div className="box mb-4">
        <div className="boxHeader">
          <h1>Commission Report Ledger</h1>
          <div className="left d-lg-flex align-items-center">
            <FilterArea
              updateFilter={updateFilter}
              filters={filters}
              updateMultipleFilters={updateMultipleFilters}
            />
          </div>
          {/* <DownloadButton />
          <FormButton
            onClick={() => setIsAddAreaOpen(true)}
            button_name="Add Area"
          /> */}

          <Modal
            type="modal-lg"
            show={isAddAreaOpen}
            onHide={() => onClose(false)}
            modalName={MODALS.ADD_AREA_MODAL}
            title={rowDataForEdit ? "Edit Area" : "Add Area"}
            body={<AddArea rowData={rowDataForEdit} onClose={onClose} />}
          />

          <Modal
            show={isDelete !== -1}
            onHide={() => onCloseDelete()}
            modalName={MODALS.DELETE_AREA_MODAL}
            title={"Are you sure you want to delete this area?"}
            footer={<Delete onClose={onCloseDelete} item={isDelete} />}
          />
        </div>

        <div className="row mb-4 mt-4 highlightsBox">
          <div class="col-12 col-sm-6 col-md-4 col-lg col-xl">
            <div className="highlight">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M23.2299 11.7158C19.5229 11.7158 17.197 11.7158 13.49 11.7158C13.381 11.1331 12.9449 10.6961 12.3634 10.5504V5.0512C12.3634 4.83267 12.218 4.68704 12 4.68704C11.782 4.68704 11.6366 4.83272 11.6366 5.0512V10.5504C11.0551 10.6961 10.619 11.1331 10.51 11.7158C6.80304 11.7158 4.44077 11.7158 0.770159 11.7158L0.661122 11.9343C0.624777 12.0436 0.588431 12.1529 0.588431 12.2621V13.8645C0.588431 14.2651 0.915493 14.6293 1.31529 14.7021L3.20512 14.9571C3.24147 14.9571 3.27781 14.9935 3.31416 15.0299C3.53223 15.9768 3.93198 16.8873 4.47712 17.6885C4.33174 17.8706 4.07737 18.1984 3.82295 18.4897L3.27781 19.1817C3.02339 19.5095 3.05974 19.9829 3.3505 20.2743L4.51346 21.4032C4.80423 21.6946 5.27667 21.731 5.60373 21.4761L7.13015 20.3471C7.1665 20.3107 7.23919 20.3107 7.27553 20.3471C8.11143 20.857 9.01998 21.2212 9.96491 21.4397C10.0013 21.4397 10.0376 21.4761 10.074 21.5125L10.3284 23.3334C10.3647 23.4791 10.4011 23.6248 10.5101 23.734C10.6555 23.9161 10.9099 24.0254 11.1643 24.0254H12.7997C13.0541 24.0254 13.3085 23.9161 13.4538 23.734C13.5629 23.6248 13.6356 23.4791 13.6356 23.3334L13.89 21.5125C13.89 21.4761 13.9263 21.4397 13.999 21.4397C14.9803 21.2211 15.8889 20.857 16.6884 20.3471C16.7248 20.3107 16.7974 20.3107 16.8338 20.3471L18.3239 21.4397C18.6873 21.6946 19.1598 21.6582 19.4868 21.3668L20.6134 20.2743C20.9042 19.9829 20.9405 19.5095 20.6861 19.1817L19.5232 17.7249C19.4868 17.6885 19.4868 17.6521 19.5232 17.6157C20.032 16.8145 20.4317 15.9404 20.6498 14.9935C20.6498 14.9571 20.6861 14.9207 20.7588 14.9207L22.6487 14.6657C23.0484 14.6293 23.3755 14.2651 23.3755 13.8281V12.2257C23.3755 12.1164 23.3392 12.0071 23.3028 11.8979L23.2299 11.7158ZM11.2368 11.9343C11.2368 11.5338 11.6002 11.2424 12 11.2424C12.3997 11.2424 12.7268 11.5337 12.7632 11.9343V12.0436C12.7268 12.4078 12.3998 12.7356 12 12.7356C11.6002 12.7356 11.2731 12.4442 11.2368 12.0436V11.9343ZM7.31178 12.4442H10.5826C10.7644 13.0269 11.3459 13.4639 12 13.4639C12.6542 13.4639 13.2357 13.0269 13.4174 12.4442H16.6883C16.7246 12.6627 16.7246 12.8448 16.7246 13.0633C16.7246 15.5762 14.6167 17.6157 12.0001 17.6157C9.38343 17.6157 7.27553 15.5762 7.27553 13.0633C7.27544 12.8448 7.27544 12.6627 7.31178 12.4442ZM22.6484 13.8645C22.6484 13.901 22.6121 13.9374 22.5394 13.9738L20.6495 14.2288C20.2861 14.2652 20.0317 14.5201 19.9227 14.8843C19.7046 15.722 19.3775 16.5231 18.9051 17.2515C18.7234 17.5428 18.7234 17.9435 18.9415 18.1984L20.1044 19.6551C20.1408 19.6916 20.1408 19.7644 20.1044 19.7644L18.9778 20.857C18.9051 20.9298 18.8324 20.9298 18.7597 20.857L17.2697 19.7644C16.9789 19.5459 16.6155 19.5459 16.3248 19.728C15.5616 20.2014 14.7257 20.5292 13.8535 20.7113C13.49 20.7841 13.2356 21.0754 13.1993 21.4032L12.9449 23.2242C12.9449 23.2242 12.9449 23.2606 12.9085 23.2606C12.9085 23.3334 12.8722 23.3334 12.8358 23.3334H11.2004C11.1641 23.3334 11.1277 23.3334 11.0914 23.297C11.0914 23.2606 11.055 23.2606 11.055 23.2606L10.8006 21.4397C10.7643 21.1119 10.4736 20.8205 10.1464 20.7477C9.2742 20.5656 8.43835 20.2014 7.67514 19.7644C7.38443 19.5823 6.98462 19.6187 6.73025 19.8008L5.20388 20.9298C5.16754 20.9662 5.09485 20.9662 5.02216 20.9298L3.85919 19.8008C3.82285 19.7644 3.82285 19.728 3.85919 19.6916L4.40433 18.9996C4.76774 18.5626 5.09485 18.1255 5.20388 18.0163L5.24023 17.9799C5.31292 17.8342 5.31292 17.6521 5.24023 17.5429C4.6951 16.7417 4.29534 15.8676 4.07727 14.9207C4.00458 14.5566 3.71386 14.338 3.35041 14.2652L1.46058 14.0102C1.38789 14.0102 1.35154 13.9738 1.35154 13.901V12.517H6.58492C6.54857 12.6627 6.54857 12.8448 6.54857 13.0633C6.54857 15.9768 8.98354 18.344 12 18.344C15.0164 18.344 17.4514 15.9768 17.4514 13.0633C17.4514 12.8812 17.4514 12.6627 17.415 12.4442H22.6484V13.8645Z"
                  fill="white"
                />
                <path
                  d="M23.9931 9.38501C22.7574 3.95863 17.7058 0.0253906 12 0.0253906C6.29414 0.0253906 1.24254 3.95863 0.0068876 9.34859C-0.029458 9.5307 0.0795787 9.74918 0.297604 9.7856C0.479332 9.85844 0.697357 9.71275 0.733703 9.53065C1.89671 4.43206 6.62125 0.753774 12 0.753774C17.3787 0.753774 22.1396 4.46848 23.2662 9.56712C23.3026 9.74922 23.4479 9.85849 23.6296 9.85849C23.666 9.85849 23.666 9.85849 23.7023 9.85849C23.9204 9.7856 24.0294 9.60354 23.9931 9.38501Z"
                  fill="white"
                />
                <path
                  d="M12 3.41231C12.218 3.41231 12.3634 3.26663 12.3634 3.04815V1.84628C12.3634 1.62776 12.218 1.48212 12 1.48212C11.7819 1.48212 11.6366 1.6278 11.6366 1.84628V3.08452C11.6366 3.26663 11.7819 3.41231 12 3.41231Z"
                  fill="white"
                />
                <path
                  d="M18.687 5.92526L19.5593 5.05118C19.7046 4.9055 19.7046 4.68701 19.5593 4.54133C19.4139 4.39564 19.1959 4.39564 19.0505 4.54133L18.1782 5.4154C18.0328 5.56109 18.0328 5.77957 18.1782 5.92526C18.3236 6.07089 18.5417 6.07089 18.687 5.92526Z"
                  fill="white"
                />
                <path
                  d="M5.82167 5.92526C5.96706 5.77957 5.96706 5.56109 5.82167 5.4154L4.94943 4.54133C4.80405 4.39564 4.58602 4.39564 4.44064 4.54133C4.29526 4.68701 4.29526 4.9055 4.44064 5.05118L5.31288 5.92526C5.42192 6.07089 5.67629 6.07089 5.82167 5.92526Z"
                  fill="white"
                />
                <path
                  d="M3.49578 8.76588C3.67751 8.83873 3.89553 8.76588 3.96822 8.58377C4.04092 8.40167 3.96822 8.18319 3.7865 8.11034L2.62353 7.63691C2.44181 7.56407 2.22378 7.63691 2.15109 7.81902C2.0784 8.00113 2.15109 8.21961 2.33282 8.29245L3.49578 8.76588Z"
                  fill="white"
                />
                <path
                  d="M15.9613 3.95864L16.4701 2.82967C16.5428 2.64757 16.4701 2.42909 16.2884 2.35624C16.1067 2.2834 15.8886 2.35624 15.8159 2.53835L15.3072 3.66732C15.2345 3.84943 15.3072 4.06791 15.4889 4.14075C15.6706 4.21355 15.8886 4.1407 15.9613 3.95864Z"
                  fill="white"
                />
                <path
                  d="M8.4747 4.14074C8.65643 4.06789 8.72912 3.84941 8.65643 3.6673L8.14764 2.53833C8.07495 2.35622 7.85693 2.28338 7.6752 2.35622C7.49347 2.42907 7.42078 2.64755 7.49347 2.82966L8.00226 3.95863C8.07495 4.14074 8.29298 4.25 8.4747 4.14074Z"
                  fill="white"
                />
                <path
                  d="M20.4678 8.7295L21.6308 8.21965C21.8125 8.14681 21.8852 7.92832 21.8125 7.74622C21.7398 7.56411 21.5218 7.49126 21.3401 7.56411L20.2135 8.03754C20.0318 8.11038 19.9591 8.32891 20.0318 8.51097C20.068 8.7295 20.2861 8.80235 20.4678 8.7295Z"
                  fill="white"
                />
              </svg>
              <div className="info">
                <div className="title">Today Commission</div>
                <div className="numbers">
                  {cardsData?.data?.today_commission || 0}
                </div>
              </div>
            </div>
          </div>
          <div class="col-12 col-sm-6 col-md-4 col-lg col-xl">
            <div className="highlight">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M23.2299 11.7158C19.5229 11.7158 17.197 11.7158 13.49 11.7158C13.381 11.1331 12.9449 10.6961 12.3634 10.5504V5.0512C12.3634 4.83267 12.218 4.68704 12 4.68704C11.782 4.68704 11.6366 4.83272 11.6366 5.0512V10.5504C11.0551 10.6961 10.619 11.1331 10.51 11.7158C6.80304 11.7158 4.44077 11.7158 0.770159 11.7158L0.661122 11.9343C0.624777 12.0436 0.588431 12.1529 0.588431 12.2621V13.8645C0.588431 14.2651 0.915493 14.6293 1.31529 14.7021L3.20512 14.9571C3.24147 14.9571 3.27781 14.9935 3.31416 15.0299C3.53223 15.9768 3.93198 16.8873 4.47712 17.6885C4.33174 17.8706 4.07737 18.1984 3.82295 18.4897L3.27781 19.1817C3.02339 19.5095 3.05974 19.9829 3.3505 20.2743L4.51346 21.4032C4.80423 21.6946 5.27667 21.731 5.60373 21.4761L7.13015 20.3471C7.1665 20.3107 7.23919 20.3107 7.27553 20.3471C8.11143 20.857 9.01998 21.2212 9.96491 21.4397C10.0013 21.4397 10.0376 21.4761 10.074 21.5125L10.3284 23.3334C10.3647 23.4791 10.4011 23.6248 10.5101 23.734C10.6555 23.9161 10.9099 24.0254 11.1643 24.0254H12.7997C13.0541 24.0254 13.3085 23.9161 13.4538 23.734C13.5629 23.6248 13.6356 23.4791 13.6356 23.3334L13.89 21.5125C13.89 21.4761 13.9263 21.4397 13.999 21.4397C14.9803 21.2211 15.8889 20.857 16.6884 20.3471C16.7248 20.3107 16.7974 20.3107 16.8338 20.3471L18.3239 21.4397C18.6873 21.6946 19.1598 21.6582 19.4868 21.3668L20.6134 20.2743C20.9042 19.9829 20.9405 19.5095 20.6861 19.1817L19.5232 17.7249C19.4868 17.6885 19.4868 17.6521 19.5232 17.6157C20.032 16.8145 20.4317 15.9404 20.6498 14.9935C20.6498 14.9571 20.6861 14.9207 20.7588 14.9207L22.6487 14.6657C23.0484 14.6293 23.3755 14.2651 23.3755 13.8281V12.2257C23.3755 12.1164 23.3392 12.0071 23.3028 11.8979L23.2299 11.7158ZM11.2368 11.9343C11.2368 11.5338 11.6002 11.2424 12 11.2424C12.3997 11.2424 12.7268 11.5337 12.7632 11.9343V12.0436C12.7268 12.4078 12.3998 12.7356 12 12.7356C11.6002 12.7356 11.2731 12.4442 11.2368 12.0436V11.9343ZM7.31178 12.4442H10.5826C10.7644 13.0269 11.3459 13.4639 12 13.4639C12.6542 13.4639 13.2357 13.0269 13.4174 12.4442H16.6883C16.7246 12.6627 16.7246 12.8448 16.7246 13.0633C16.7246 15.5762 14.6167 17.6157 12.0001 17.6157C9.38343 17.6157 7.27553 15.5762 7.27553 13.0633C7.27544 12.8448 7.27544 12.6627 7.31178 12.4442ZM22.6484 13.8645C22.6484 13.901 22.6121 13.9374 22.5394 13.9738L20.6495 14.2288C20.2861 14.2652 20.0317 14.5201 19.9227 14.8843C19.7046 15.722 19.3775 16.5231 18.9051 17.2515C18.7234 17.5428 18.7234 17.9435 18.9415 18.1984L20.1044 19.6551C20.1408 19.6916 20.1408 19.7644 20.1044 19.7644L18.9778 20.857C18.9051 20.9298 18.8324 20.9298 18.7597 20.857L17.2697 19.7644C16.9789 19.5459 16.6155 19.5459 16.3248 19.728C15.5616 20.2014 14.7257 20.5292 13.8535 20.7113C13.49 20.7841 13.2356 21.0754 13.1993 21.4032L12.9449 23.2242C12.9449 23.2242 12.9449 23.2606 12.9085 23.2606C12.9085 23.3334 12.8722 23.3334 12.8358 23.3334H11.2004C11.1641 23.3334 11.1277 23.3334 11.0914 23.297C11.0914 23.2606 11.055 23.2606 11.055 23.2606L10.8006 21.4397C10.7643 21.1119 10.4736 20.8205 10.1464 20.7477C9.2742 20.5656 8.43835 20.2014 7.67514 19.7644C7.38443 19.5823 6.98462 19.6187 6.73025 19.8008L5.20388 20.9298C5.16754 20.9662 5.09485 20.9662 5.02216 20.9298L3.85919 19.8008C3.82285 19.7644 3.82285 19.728 3.85919 19.6916L4.40433 18.9996C4.76774 18.5626 5.09485 18.1255 5.20388 18.0163L5.24023 17.9799C5.31292 17.8342 5.31292 17.6521 5.24023 17.5429C4.6951 16.7417 4.29534 15.8676 4.07727 14.9207C4.00458 14.5566 3.71386 14.338 3.35041 14.2652L1.46058 14.0102C1.38789 14.0102 1.35154 13.9738 1.35154 13.901V12.517H6.58492C6.54857 12.6627 6.54857 12.8448 6.54857 13.0633C6.54857 15.9768 8.98354 18.344 12 18.344C15.0164 18.344 17.4514 15.9768 17.4514 13.0633C17.4514 12.8812 17.4514 12.6627 17.415 12.4442H22.6484V13.8645Z"
                  fill="white"
                />
                <path
                  d="M23.9931 9.38501C22.7574 3.95863 17.7058 0.0253906 12 0.0253906C6.29414 0.0253906 1.24254 3.95863 0.0068876 9.34859C-0.029458 9.5307 0.0795787 9.74918 0.297604 9.7856C0.479332 9.85844 0.697357 9.71275 0.733703 9.53065C1.89671 4.43206 6.62125 0.753774 12 0.753774C17.3787 0.753774 22.1396 4.46848 23.2662 9.56712C23.3026 9.74922 23.4479 9.85849 23.6296 9.85849C23.666 9.85849 23.666 9.85849 23.7023 9.85849C23.9204 9.7856 24.0294 9.60354 23.9931 9.38501Z"
                  fill="white"
                />
                <path
                  d="M12 3.41231C12.218 3.41231 12.3634 3.26663 12.3634 3.04815V1.84628C12.3634 1.62776 12.218 1.48212 12 1.48212C11.7819 1.48212 11.6366 1.6278 11.6366 1.84628V3.08452C11.6366 3.26663 11.7819 3.41231 12 3.41231Z"
                  fill="white"
                />
                <path
                  d="M18.687 5.92526L19.5593 5.05118C19.7046 4.9055 19.7046 4.68701 19.5593 4.54133C19.4139 4.39564 19.1959 4.39564 19.0505 4.54133L18.1782 5.4154C18.0328 5.56109 18.0328 5.77957 18.1782 5.92526C18.3236 6.07089 18.5417 6.07089 18.687 5.92526Z"
                  fill="white"
                />
                <path
                  d="M5.82167 5.92526C5.96706 5.77957 5.96706 5.56109 5.82167 5.4154L4.94943 4.54133C4.80405 4.39564 4.58602 4.39564 4.44064 4.54133C4.29526 4.68701 4.29526 4.9055 4.44064 5.05118L5.31288 5.92526C5.42192 6.07089 5.67629 6.07089 5.82167 5.92526Z"
                  fill="white"
                />
                <path
                  d="M3.49578 8.76588C3.67751 8.83873 3.89553 8.76588 3.96822 8.58377C4.04092 8.40167 3.96822 8.18319 3.7865 8.11034L2.62353 7.63691C2.44181 7.56407 2.22378 7.63691 2.15109 7.81902C2.0784 8.00113 2.15109 8.21961 2.33282 8.29245L3.49578 8.76588Z"
                  fill="white"
                />
                <path
                  d="M15.9613 3.95864L16.4701 2.82967C16.5428 2.64757 16.4701 2.42909 16.2884 2.35624C16.1067 2.2834 15.8886 2.35624 15.8159 2.53835L15.3072 3.66732C15.2345 3.84943 15.3072 4.06791 15.4889 4.14075C15.6706 4.21355 15.8886 4.1407 15.9613 3.95864Z"
                  fill="white"
                />
                <path
                  d="M8.4747 4.14074C8.65643 4.06789 8.72912 3.84941 8.65643 3.6673L8.14764 2.53833C8.07495 2.35622 7.85693 2.28338 7.6752 2.35622C7.49347 2.42907 7.42078 2.64755 7.49347 2.82966L8.00226 3.95863C8.07495 4.14074 8.29298 4.25 8.4747 4.14074Z"
                  fill="white"
                />
                <path
                  d="M20.4678 8.7295L21.6308 8.21965C21.8125 8.14681 21.8852 7.92832 21.8125 7.74622C21.7398 7.56411 21.5218 7.49126 21.3401 7.56411L20.2135 8.03754C20.0318 8.11038 19.9591 8.32891 20.0318 8.51097C20.068 8.7295 20.2861 8.80235 20.4678 8.7295Z"
                  fill="white"
                />
              </svg>
              <div className="info">
                <div className="title">Monthly Commission</div>
                <div className="numbers">
                  {cardsData?.data?.monthly_commission || 0}
                </div>
              </div>
            </div>
          </div>
          <div class="col-12 col-sm-6 col-md-4 col-lg col-xl">
            <div className="highlight">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M23.2299 11.7158C19.5229 11.7158 17.197 11.7158 13.49 11.7158C13.381 11.1331 12.9449 10.6961 12.3634 10.5504V5.0512C12.3634 4.83267 12.218 4.68704 12 4.68704C11.782 4.68704 11.6366 4.83272 11.6366 5.0512V10.5504C11.0551 10.6961 10.619 11.1331 10.51 11.7158C6.80304 11.7158 4.44077 11.7158 0.770159 11.7158L0.661122 11.9343C0.624777 12.0436 0.588431 12.1529 0.588431 12.2621V13.8645C0.588431 14.2651 0.915493 14.6293 1.31529 14.7021L3.20512 14.9571C3.24147 14.9571 3.27781 14.9935 3.31416 15.0299C3.53223 15.9768 3.93198 16.8873 4.47712 17.6885C4.33174 17.8706 4.07737 18.1984 3.82295 18.4897L3.27781 19.1817C3.02339 19.5095 3.05974 19.9829 3.3505 20.2743L4.51346 21.4032C4.80423 21.6946 5.27667 21.731 5.60373 21.4761L7.13015 20.3471C7.1665 20.3107 7.23919 20.3107 7.27553 20.3471C8.11143 20.857 9.01998 21.2212 9.96491 21.4397C10.0013 21.4397 10.0376 21.4761 10.074 21.5125L10.3284 23.3334C10.3647 23.4791 10.4011 23.6248 10.5101 23.734C10.6555 23.9161 10.9099 24.0254 11.1643 24.0254H12.7997C13.0541 24.0254 13.3085 23.9161 13.4538 23.734C13.5629 23.6248 13.6356 23.4791 13.6356 23.3334L13.89 21.5125C13.89 21.4761 13.9263 21.4397 13.999 21.4397C14.9803 21.2211 15.8889 20.857 16.6884 20.3471C16.7248 20.3107 16.7974 20.3107 16.8338 20.3471L18.3239 21.4397C18.6873 21.6946 19.1598 21.6582 19.4868 21.3668L20.6134 20.2743C20.9042 19.9829 20.9405 19.5095 20.6861 19.1817L19.5232 17.7249C19.4868 17.6885 19.4868 17.6521 19.5232 17.6157C20.032 16.8145 20.4317 15.9404 20.6498 14.9935C20.6498 14.9571 20.6861 14.9207 20.7588 14.9207L22.6487 14.6657C23.0484 14.6293 23.3755 14.2651 23.3755 13.8281V12.2257C23.3755 12.1164 23.3392 12.0071 23.3028 11.8979L23.2299 11.7158ZM11.2368 11.9343C11.2368 11.5338 11.6002 11.2424 12 11.2424C12.3997 11.2424 12.7268 11.5337 12.7632 11.9343V12.0436C12.7268 12.4078 12.3998 12.7356 12 12.7356C11.6002 12.7356 11.2731 12.4442 11.2368 12.0436V11.9343ZM7.31178 12.4442H10.5826C10.7644 13.0269 11.3459 13.4639 12 13.4639C12.6542 13.4639 13.2357 13.0269 13.4174 12.4442H16.6883C16.7246 12.6627 16.7246 12.8448 16.7246 13.0633C16.7246 15.5762 14.6167 17.6157 12.0001 17.6157C9.38343 17.6157 7.27553 15.5762 7.27553 13.0633C7.27544 12.8448 7.27544 12.6627 7.31178 12.4442ZM22.6484 13.8645C22.6484 13.901 22.6121 13.9374 22.5394 13.9738L20.6495 14.2288C20.2861 14.2652 20.0317 14.5201 19.9227 14.8843C19.7046 15.722 19.3775 16.5231 18.9051 17.2515C18.7234 17.5428 18.7234 17.9435 18.9415 18.1984L20.1044 19.6551C20.1408 19.6916 20.1408 19.7644 20.1044 19.7644L18.9778 20.857C18.9051 20.9298 18.8324 20.9298 18.7597 20.857L17.2697 19.7644C16.9789 19.5459 16.6155 19.5459 16.3248 19.728C15.5616 20.2014 14.7257 20.5292 13.8535 20.7113C13.49 20.7841 13.2356 21.0754 13.1993 21.4032L12.9449 23.2242C12.9449 23.2242 12.9449 23.2606 12.9085 23.2606C12.9085 23.3334 12.8722 23.3334 12.8358 23.3334H11.2004C11.1641 23.3334 11.1277 23.3334 11.0914 23.297C11.0914 23.2606 11.055 23.2606 11.055 23.2606L10.8006 21.4397C10.7643 21.1119 10.4736 20.8205 10.1464 20.7477C9.2742 20.5656 8.43835 20.2014 7.67514 19.7644C7.38443 19.5823 6.98462 19.6187 6.73025 19.8008L5.20388 20.9298C5.16754 20.9662 5.09485 20.9662 5.02216 20.9298L3.85919 19.8008C3.82285 19.7644 3.82285 19.728 3.85919 19.6916L4.40433 18.9996C4.76774 18.5626 5.09485 18.1255 5.20388 18.0163L5.24023 17.9799C5.31292 17.8342 5.31292 17.6521 5.24023 17.5429C4.6951 16.7417 4.29534 15.8676 4.07727 14.9207C4.00458 14.5566 3.71386 14.338 3.35041 14.2652L1.46058 14.0102C1.38789 14.0102 1.35154 13.9738 1.35154 13.901V12.517H6.58492C6.54857 12.6627 6.54857 12.8448 6.54857 13.0633C6.54857 15.9768 8.98354 18.344 12 18.344C15.0164 18.344 17.4514 15.9768 17.4514 13.0633C17.4514 12.8812 17.4514 12.6627 17.415 12.4442H22.6484V13.8645Z"
                  fill="white"
                />
                <path
                  d="M23.9931 9.38501C22.7574 3.95863 17.7058 0.0253906 12 0.0253906C6.29414 0.0253906 1.24254 3.95863 0.0068876 9.34859C-0.029458 9.5307 0.0795787 9.74918 0.297604 9.7856C0.479332 9.85844 0.697357 9.71275 0.733703 9.53065C1.89671 4.43206 6.62125 0.753774 12 0.753774C17.3787 0.753774 22.1396 4.46848 23.2662 9.56712C23.3026 9.74922 23.4479 9.85849 23.6296 9.85849C23.666 9.85849 23.666 9.85849 23.7023 9.85849C23.9204 9.7856 24.0294 9.60354 23.9931 9.38501Z"
                  fill="white"
                />
                <path
                  d="M12 3.41231C12.218 3.41231 12.3634 3.26663 12.3634 3.04815V1.84628C12.3634 1.62776 12.218 1.48212 12 1.48212C11.7819 1.48212 11.6366 1.6278 11.6366 1.84628V3.08452C11.6366 3.26663 11.7819 3.41231 12 3.41231Z"
                  fill="white"
                />
                <path
                  d="M18.687 5.92526L19.5593 5.05118C19.7046 4.9055 19.7046 4.68701 19.5593 4.54133C19.4139 4.39564 19.1959 4.39564 19.0505 4.54133L18.1782 5.4154C18.0328 5.56109 18.0328 5.77957 18.1782 5.92526C18.3236 6.07089 18.5417 6.07089 18.687 5.92526Z"
                  fill="white"
                />
                <path
                  d="M5.82167 5.92526C5.96706 5.77957 5.96706 5.56109 5.82167 5.4154L4.94943 4.54133C4.80405 4.39564 4.58602 4.39564 4.44064 4.54133C4.29526 4.68701 4.29526 4.9055 4.44064 5.05118L5.31288 5.92526C5.42192 6.07089 5.67629 6.07089 5.82167 5.92526Z"
                  fill="white"
                />
                <path
                  d="M3.49578 8.76588C3.67751 8.83873 3.89553 8.76588 3.96822 8.58377C4.04092 8.40167 3.96822 8.18319 3.7865 8.11034L2.62353 7.63691C2.44181 7.56407 2.22378 7.63691 2.15109 7.81902C2.0784 8.00113 2.15109 8.21961 2.33282 8.29245L3.49578 8.76588Z"
                  fill="white"
                />
                <path
                  d="M15.9613 3.95864L16.4701 2.82967C16.5428 2.64757 16.4701 2.42909 16.2884 2.35624C16.1067 2.2834 15.8886 2.35624 15.8159 2.53835L15.3072 3.66732C15.2345 3.84943 15.3072 4.06791 15.4889 4.14075C15.6706 4.21355 15.8886 4.1407 15.9613 3.95864Z"
                  fill="white"
                />
                <path
                  d="M8.4747 4.14074C8.65643 4.06789 8.72912 3.84941 8.65643 3.6673L8.14764 2.53833C8.07495 2.35622 7.85693 2.28338 7.6752 2.35622C7.49347 2.42907 7.42078 2.64755 7.49347 2.82966L8.00226 3.95863C8.07495 4.14074 8.29298 4.25 8.4747 4.14074Z"
                  fill="white"
                />
                <path
                  d="M20.4678 8.7295L21.6308 8.21965C21.8125 8.14681 21.8852 7.92832 21.8125 7.74622C21.7398 7.56411 21.5218 7.49126 21.3401 7.56411L20.2135 8.03754C20.0318 8.11038 19.9591 8.32891 20.0318 8.51097C20.068 8.7295 20.2861 8.80235 20.4678 8.7295Z"
                  fill="white"
                />
              </svg>
              <div className="info">
                <div className="title">Total Commission</div>
                <div className="numbers">
                  {cardsData?.data?.total_commission || 0}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="boxBody">
          <Table
            data={tableData || []}
            isLoading={isLoading}
            tableColumns={columns}
            currentPage={currentPage}
            totalPages={totalPages}
            itemPerPage={itemPerPage}
            disablePrev={disablePrev}
            disableNext={disableNext}
            disableFirst={disableFirst}
            disableLast={disableLast}
            loadFirstPage={loadFirstPage}
            loadLastPage={loadLastPage}
            totalRecords={5}
            loadNextPage={loadNextPage}
            loadPreviousPage={loadPreviousPage}
            showPagination={false}
          />
        </div>
      </div>
    </div>
  );
};

export default CommissionReportLedger;
